import { useMutation } from "@tanstack/react-query";
import { API_BASE_URL } from "../../config";
import { getAccessToken } from "../../API/fetchClient";

export interface CsvDownloadRequest {
  siteId: number;
  filename?: string;
}

export const useDownloadCsvTemplate = () => {
  return useMutation({
    mutationFn: async ({ siteId, filename }: CsvDownloadRequest): Promise<void> => {
      if (!siteId || isNaN(siteId)) {
        throw new Error("Invalid site ID provided");
      }

      const token = getAccessToken();
      if (!token) {
        throw new Error("Aucun token d'authentification trouvé. Veuillez vous reconnecter.");
      }

      const response = await fetch(`${API_BASE_URL}/core/saisies-csv/${siteId}/download_csv/`, {
        method: "GET",
        headers: {
          Authorization: `Bearer ${token}`,
          Accept:
            "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet,application/vnd.ms-excel,*/*",
        },
        credentials: "include",
      });

      if (!response.ok) {
        if (response.status === 401) throw new Error("Non autorisé. Veuillez vous reconnecter.");
        if (response.status === 404)
          throw new Error(`Site avec l'ID ${siteId} non trouvé ou template non disponible.`);
        if (response.status === 500)
          throw new Error("Erreur interne du serveur. Veuillez réessayer plus tard.");
        throw new Error(
          `Erreur lors du téléchargement: ${response.status} ${response.statusText}`
        );
      }

      const blob = await response.blob();
      if (blob.size === 0) throw new Error("Le fichier téléchargé est vide.");

      let downloadFilename = filename || `template_saisie_site_${siteId}.xlsx`;
      const contentDisposition = response.headers.get("content-disposition");
      if (contentDisposition) {
        const match = contentDisposition.match(/filename[^;=\n]*=((['"]).*?\2|[^;\n]*)/);
        if (match?.[1]) downloadFilename = match[1].replace(/['"]/g, "");
      }

      const downloadUrl = window.URL.createObjectURL(blob);
      const link = document.createElement("a");
      link.href = downloadUrl;
      link.download = downloadFilename;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(downloadUrl);
    },
  });
};

export default useDownloadCsvTemplate;
